import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  useTheme,
  alpha,
  Divider,
  Stack,
  Badge,
  Tooltip,
  IconButton,
  Snackbar
} from '@mui/material';
import {
  Star, Warning, Info, CheckCircle, Upgrade, Cancel, Pause, PlayArrow, History, Receipt, CreditCard, TrendingUp, TrendingDown, Refresh, Settings, Support, Security, Timer, AutoAwesome, Diamond, Bolt, HelpOutline, Warning as WarningIcon
} from '@mui/icons-material';
import { useSubscription } from '../hooks/useSubscription';
import axiosInstance from '../api/axiosInstance';

// Check if Razorpay is available
const isRazorpayAvailable = () => {
  const available = typeof window !== 'undefined' && window.Razorpay;
  if (available) {
  }
  return available;
};

const FeatureTooltip = ({ feature, description }) => (
  <Box display="flex" alignItems="center" gap={1}>
    <Typography>{feature}</Typography>
    <Tooltip title={description} arrow placement="right">
      <IconButton size="small">
        <HelpOutline />
      </IconButton>
    </Tooltip>
  </Box>
);

const ConfirmationDialog = ({ open, title, message, onConfirm, onCancel, action }) => (
  <Dialog open={open} onClose={onCancel}>
    <DialogTitle>{title}</DialogTitle>
    <DialogContent>
      <Typography>{message}</Typography>
      {action === 'downgrade' && (
        <Box mt={2} p={2} bgcolor="warning.light" borderRadius={1}>
          <Typography variant="body2" color="warning.dark" display="flex" alignItems="center" gap={1}>
            <WarningIcon fontSize="small" />
            Downgrading will limit your access to premium features immediately.
          </Typography>
        </Box>
      )}
    </DialogContent>
    <DialogActions>
      <Button onClick={onCancel}>Cancel</Button>
      <Button 
        onClick={onConfirm} 
        color={action === 'downgrade' ? 'warning' : 'primary'}
        variant="contained"
      >
        Confirm {action}
      </Button>
    </DialogActions>
  </Dialog>
);

const PlanCard = ({ plan, currentPlan, onSelect, isLoading, getActionButton }) => {
  const theme = useTheme();
  const isCurrentPlan = currentPlan?.id === plan.id;
  const isPremium = plan.plan_type === 'professional';
  const actionButton = getActionButton(plan);

  return (
    <Card
      sx={{
        p: 3,
        border: isCurrentPlan ? `2px solid ${theme.palette.primary.main}` : '1px solid',
        borderColor: isCurrentPlan ? theme.palette.primary.main : 'divider',
        position: 'relative',
        opacity: isLoading ? 0.7 : 1,
        pointerEvents: isLoading ? 'none' : 'auto'
      }}
    >
      {isCurrentPlan && (
        <Box
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            bgcolor: 'primary.main',
            color: 'white',
            px: 1,
            py: 0.5,
            borderRadius: 1,
            fontSize: '0.75rem'
          }}
        >
          Current Plan
        </Box>
      )}
      
      <Typography variant="h5" gutterBottom>
        {plan.name}
      </Typography>
      
      <Typography variant="h4" gutterBottom>
        ₹{plan.price}/mo
      </Typography>

      <List dense>
        {plan.features.slice(0, 5).map((feature, index) => (
          <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <CheckCircle color="success" sx={{ fontSize: 18 }} />
            </ListItemIcon>
            <ListItemText 
              primary={feature} 
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </ListItem>
        ))}
        {plan.features.length > 5 && (
          <ListItem sx={{ px: 0, py: 0.5 }}>
            <ListItemText 
              primary={`+${plan.features.length - 5} more features`}
              primaryTypographyProps={{ 
                variant: 'body2', 
                color: 'primary.main',
                fontWeight: 600
              }}
            />
          </ListItem>
        )}
      </List>

      <Box mt={3}>
        <Button
          variant="contained"
          color={actionButton.color || (isPremium ? 'primary' : 'secondary')}
          fullWidth
          onClick={() => onSelect(plan)}
          disabled={isLoading || actionButton.disabled}
          sx={{ position: 'relative' }}
        >
          {isLoading ? (
            <CircularProgress size={24} sx={{ position: 'absolute' }} />
          ) : (
            actionButton.text
          )}
        </Button>
      </Box>
    </Card>
  );
};

const SubscriptionManagement = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  
  const {
    subscriptionStatus, loading, error, hasActiveSubscription, isOnTrial, getPlanType, getDaysRemaining, getCurrentUsage, getUsagePercentage, isUsageLimitReached, isUsageLimitNear, canAccessFeature, getUpgradeRecommendations, refresh, isAuthenticated
  } = useSubscription();

  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [billingHistory, setBillingHistory] = useState([]);
  
  // Enhanced states for better UX
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [planComparisonDialog, setPlanComparisonDialog] = useState(false);
  const [planComparison, setPlanComparison] = useState(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [trialStatus, setTrialStatus] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, plan: null });
  const [isLoading, setIsLoading] = useState(false);

  // Fetch plans and billing history
  useEffect(() => {
    const fetchData = async () => {
      if (!isAuthenticated) return;
      
      try {
        // Fetch plans
        const plansResponse = await axiosInstance.get('/payments/plans/');
        setPlans(plansResponse.data);
        
        // Fetch billing history
        const historyResponse = await axiosInstance.get('/payments/history/');
        setBillingHistory(historyResponse.data);
        
        // Fetch trial status
        const trialResponse = await axiosInstance.get('/payments/trial/');
        setTrialStatus(trialResponse.data);
      } catch (err) {
        console.error('Error fetching subscription data:', err);
      }
    };
    
    fetchData();
  }, [isAuthenticated]);

  // Handle subscription actions
  const handleSubscriptionAction = async (action, planId = null) => {
    setActionLoading(true);
    try {
      let endpoint = '';
      let payload = {};
      
      switch (action) {
        case 'upgrade':
          endpoint = '/payments/subscription/upgrade/';
          payload = { plan_id: planId };
          break;
        case 'downgrade':
          endpoint = '/payments/subscription/downgrade/';
          payload = { plan_id: planId };
          break;
        case 'cancel':
          endpoint = '/payments/subscription/cancel/';
          break;
        case 'pause':
          endpoint = '/payments/subscription/pause/';
          break;
        case 'resume':
          endpoint = '/payments/subscription/resume/';
          break;
        case 'start_trial':
          endpoint = '/payments/start-trial/';
          payload = { plan_id: planId };
          break;
        default:
          throw new Error('Invalid action');
      }
      
      await axiosInstance.post(endpoint, payload);
      
      // Show success message
      setSnackbar({
        open: true,
        message: `Subscription ${action} successful!`,
        severity: 'success'
      });
      
      // Refresh subscription data
      refresh();
      
      // Close modals
      setShowUpgradeModal(false);
      setShowCancelModal(false);
      setShowPauseModal(false);
      setPlanComparisonDialog(false);
      
    } catch (err) {
      console.error(`Error performing ${action}:`, err);
      setSnackbar({
        open: true,
        message: `Failed to ${action} subscription. Please try again.`,
        severity: 'error'
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Enhanced plan selection with comparison
  const handleSelectPlan = async (plan) => {
    setSelectedPlan(plan);
    
    // For all plan changes, show plan comparison dialog first
    const action = getActionButton(plan).text.toLowerCase();
    
    if (action === 'get started' || action === 'upgrade' || action === 'renew' || action === 'downgrade') {
      // Show plan comparison dialog first
      try {
        const response = await axiosInstance.post('/payments/compare-plan/', {
          plan_id: plan.id
        });
        
        setPlanComparison(response.data);
        setPlanComparisonDialog(true);
      } catch (error) {
        // Fallback to simple dialog if comparison fails
        setSelectedPlan(plan);
        setPlanComparisonDialog(true);
      }
      return;
    }
    
    // For other actions, try to get plan comparison
    try {
      const response = await axiosInstance.post('/payments/compare-plan/', {
        plan_id: plan.id
      });
      
      setPlanComparison(response.data);
      setPlanComparisonDialog(true);
    } catch (error) {
      // Fallback to simple selection
      setSelectedPlan(plan);
      setPlanComparisonDialog(true);
    }
  };

  // Dynamic Razorpay loader
  function loadRazorpayScript() {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  }

  // Enhanced payment handler with better error handling
  const handleRazorpayPayment = async (plan) => {
    // Dynamic loader fallback
    const loaded = await loadRazorpayScript();
    
    if (!loaded) {
      setSnackbar({ open: true, message: 'Failed to load payment gateway.', severity: 'error' });
      return;
    }

    setIsProcessingPayment(true);
    setPaymentStatus('processing');
    setPlanComparisonDialog(false);
    
    try {
      const response = await axiosInstance.post('/payments/subscribe/', { plan_id: plan.id });
      
      // Validate response data
      if (!response.data.razorpay_key || !response.data.razorpay_subscription_id) {
        throw new Error('Invalid response from payment server. Missing required fields.');
      }
      
      const { subscription_id, razorpay_key, razorpay_subscription_id } = response.data;
      
      const options = {
        key: razorpay_key,
        subscription_id: razorpay_subscription_id,
        name: 'MindWell',
        description: `Subscription for ${plan.name}`,
        handler: async (rzpResponse) => {
          try {
            // Activate subscription immediately after successful payment
            await axiosInstance.post('/payments/activate/');
            
            // Refresh subscription data
            refresh();
            
            setIsProcessingPayment(false);
            setPaymentStatus('success');
            setPaymentDetails({ plan, rzpResponse });
            
            setSnackbar({
              open: true,
              message: 'Payment successful! Your subscription is now active.',
              severity: 'success'
            });
            
          } catch (error) {
            setIsProcessingPayment(false);
            setPaymentStatus('success');
            setPaymentDetails({ plan, rzpResponse });
            
            setSnackbar({
              open: true,
              message: 'Payment successful but activation may be delayed.',
              severity: 'warning'
            });
            
            // Retry activation after a delay
            setTimeout(async () => {
              try {
                await axiosInstance.post('/payments/activate/');
                refresh();
              } catch (retryError) {
                console.error('Retry activation failed:', retryError);
              }
            }, 5000);
          }
        },
        prefill: {
          name: user?.full_name || '',
          email: user?.email || '',
          contact: user?.phone || '',
        },
        theme: { color: '#1976d2' },
        modal: { ondismiss: () => setIsProcessingPayment(false) },
      };
      
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      setIsProcessingPayment(false);
      setPaymentStatus('failure');
      
      const errorMessage = error.response?.data?.error || error.message || 'Payment initiation failed. Please try again.';
      setSnackbar({
        open: true,
        message: `Payment Error: ${errorMessage}`,
        severity: 'error'
      });
    }
  };

  // Start trial
  const handleStartTrial = async () => {
    try {
      setActionLoading(true);
      await axiosInstance.post('/payments/start-trial/');
      
      setSnackbar({
        open: true,
        message: 'Trial started successfully! You now have access to professional features.',
        severity: 'success'
      });
      
      refresh();
    } catch (error) {
      console.error('Error starting trial:', error);
      setSnackbar({
        open: true,
        message: 'Failed to start trial. Please try again.',
        severity: 'error'
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Get plan color based on type
  const getPlanColor = (planType) => {
    switch (planType) {
      case 'basic': return theme.palette.primary.main;
      case 'professional': return theme.palette.secondary.main;
      case 'enterprise': return theme.palette.success.main;
      default: return theme.palette.grey[500];
    }
  };

  // Get plan icon
  const getPlanIcon = (planType) => {
    switch (planType) {
      case 'basic': return <Star />;
      case 'professional': return <Diamond />;
      case 'enterprise': return <Star />;
      default: return <Star />;
    }
  };

  // Get action button text and color
  const getActionButton = (plan) => {
    if (!hasActiveSubscription() && !isOnTrial()) {
      return {
        text: 'Get Started',
        color: 'primary',
        action: () => handleSelectPlan(plan)
      };
    }
    
    const currentPlanType = getPlanType();
    const planHierarchy = { basic: 1, professional: 2, enterprise: 3 };
    const currentTier = planHierarchy[currentPlanType] || 0;
    const targetTier = planHierarchy[plan.plan_type] || 0;
    
    if (plan.plan_type === currentPlanType) {
      return {
        text: 'Current Plan',
        color: 'success',
        disabled: true
      };
    } else if (targetTier > currentTier) {
      return {
        text: 'Upgrade',
        color: 'primary',
        action: () => handleSelectPlan(plan)
      };
    } else if (targetTier < currentTier) {
      return {
        text: 'Downgrade',
        color: 'warning',
        action: () => handleSelectPlan(plan)
      };
    } else {
      return {
        text: 'Switch Plan',
        color: 'secondary',
        action: () => handleSelectPlan(plan)
      };
    }
  };

  // Check if trial is available
  const canStartTrial = () => {
    return trialStatus?.can_start_trial && !hasActiveSubscription() && !isOnTrial();
  };

  const handlePlanAction = async (plan) => {
    // All plan actions now go through the same flow: plan comparison dialog -> Razorpay payment
    await handleSelectPlan(plan);
  };

  const handleConfirmAction = async () => {
    const { plan } = confirmDialog;
    setConfirmDialog({ open: false, action: null, plan: null });
    // All actions now use the same Razorpay flow
    await handleRazorpayPayment(plan);
  };

  const handlePlanSelection = async (plan) => {
    // This function is now simplified - all plan changes use Razorpay payment
    await handleRazorpayPayment(plan);
  };

  if (!isAuthenticated) {
    return (
      <Card sx={{ 
        border: '2px solid', 
        borderColor: 'warning.main',
        background: alpha(theme.palette.warning.main, 0.05)
      }}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <Warning sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Authentication Required
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Please log in to manage your subscription.
          </Typography>
          <Button 
            variant="contained" 
            color="warning"
            onClick={() => navigate('/login')}
            startIcon={<Security />}
            size="large"
          >
            Login Now
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={48} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading subscription information...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Enhanced Trial Banner */}
      {isOnTrial() && (
        <Alert 
          severity="info" 
          sx={{ 
            mb: 3, 
            borderRadius: 2,
            background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
            border: '1px solid #2196f3'
          }}
          icon={<Timer />}
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={() => setShowUpgradeModal(true)}
              startIcon={<Upgrade />}
            >
              Choose Plan
            </Button>
          }
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              🎉 You're on a Professional Trial!
            </Typography>
            <Typography variant="body2">
              {getDaysRemaining()} days remaining • You have access to all professional features
            </Typography>
          </Box>
        </Alert>
      )}

      {/* Current Subscription Status */}
      {(hasActiveSubscription() || isOnTrial()) && (
        <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #f5f5f5 0%, #e8f5e8 100%)' }}>
          <CardContent>
            <Box display="flex" justifyContent="between" alignItems="center" mb={2}>
              <Box display="flex" alignItems="center" gap={2}>
                <Box sx={{ 
                  p: 1, 
                  borderRadius: '50%', 
                  backgroundColor: getPlanColor(getPlanType()),
                  color: 'white'
                }}>
                  {getPlanIcon(getPlanType())}
                </Box>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {subscriptionStatus?.status?.plan_name || 'Current Plan'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {isOnTrial() ? 'Trial Period' : 'Active Subscription'} • {getDaysRemaining()} days remaining
                  </Typography>
                </Box>
              </Box>
              <Box textAlign="right">
                <Chip 
                  label={isOnTrial() ? 'TRIAL' : 'ACTIVE'}
                  color={isOnTrial() ? 'warning' : 'success'}
                  sx={{ fontWeight: 600 }}
                />
              </Box>
            </Box>
            
            {/* Usage Overview */}
            <Grid container spacing={2} sx={{ mt: 2 }}>
              {['appointments', 'patients', 'consultations'].map((feature) => {
                const usage = getCurrentUsage(feature);
                const percentage = getUsagePercentage(feature);
                const isNearLimit = isUsageLimitNear(feature);
                const isAtLimit = isUsageLimitReached(feature);
                
                return (
                  <Grid item xs={12} md={4} key={feature}>
                    <Box>
                      <Box display="flex" justifyContent="between" alignItems="center" mb={1}>
                        <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                          {feature.replace('_', ' ')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {usage} / {subscriptionStatus?.usage?.[feature]?.limit === -1 ? '∞' : subscriptionStatus?.usage?.[feature]?.limit || 0}
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={percentage} 
                        sx={{ 
                          height: 8, 
                          borderRadius: 4,
                          backgroundColor: alpha(theme.palette.grey[300], 0.3),
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: isAtLimit ? theme.palette.error.main : 
                                           isNearLimit ? theme.palette.warning.main : 
                                           theme.palette.success.main
                          }
                        }}
                      />
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Available Plans */}
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="between" alignItems="center" mb={3}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Subscription Plans
            </Typography>
            {canStartTrial() && (
              <Button
                variant="outlined"
                startIcon={<AutoAwesome />}
                onClick={handleStartTrial}
                disabled={actionLoading}
                sx={{ borderRadius: 3 }}
              >
                Start Free Trial
              </Button>
            )}
          </Box>
          
          <Box
            display="grid"
            gridTemplateColumns={{ xs: '1fr', md: 'repeat(3, 1fr)' }}
            gap={3}
            mt={3}
          >
            {plans.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                currentPlan={selectedPlan}
                onSelect={handlePlanAction}
                isLoading={isLoading}
                getActionButton={getActionButton}
              />
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* Billing History */}
      {billingHistory.length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
              Billing History
            </Typography>
            
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.1) }}>
                    <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Plan</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Invoice</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {billingHistory.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{new Date(item.date).toLocaleDateString()}</TableCell>
                      <TableCell>{item.plan}</TableCell>
                      <TableCell>{formatCurrency(item.amount)}</TableCell>
                      <TableCell>
                        <Chip 
                          label={item.status} 
                          color={item.status === 'paid' ? 'success' : 'warning'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Button size="small" startIcon={<Receipt />}>
                          Download
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Enhanced Plan Comparison Dialog */}
      <Dialog 
        open={planComparisonDialog} 
        onClose={() => setPlanComparisonDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, overflow: 'hidden' }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
          color: 'white',
          pb: 3
        }}>
          <Box display="flex" alignItems="center" gap={2}>
            {selectedPlan && getPlanIcon(selectedPlan.plan_type)}
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {planComparison?.comparison?.action_type === 'upgrade' ? 'Upgrade Plan' :
                 planComparison?.comparison?.action_type === 'downgrade' ? 'Downgrade Plan' :
                 planComparison?.comparison?.action_type === 'renew' ? 'Renew Plan' :
                 'Change Plan'}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {selectedPlan?.name}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 4 }}>
          {selectedPlan && planComparison && (
            <Box>
              {/* Plan Comparison Summary */}
              <Alert 
                severity={planComparison.comparison.is_upgrade ? 'info' : 'warning'}
                sx={{ mb: 3, borderRadius: 2 }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {planComparison.comparison.action_type === 'upgrade' && 'Upgrading your plan'}
                  {planComparison.comparison.action_type === 'downgrade' && 'Downgrading your plan'}
                  {planComparison.comparison.action_type === 'renew' && 'Renewing your current plan'}
                  {planComparison.comparison.action_type === 'new' && 'Starting new subscription'}
                </Typography>
                <Typography variant="body2">
                  {planComparison.comparison.billing_change === 'immediate' 
                    ? 'Changes will take effect immediately'
                    : 'Changes will take effect at your next billing cycle'
                  }
                </Typography>
              </Alert>

              {/* Cost Breakdown */}
              <Card sx={{ mb: 3, backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Cost Breakdown
                  </Typography>
                  
                  <Stack spacing={2}>
                    <Box display="flex" justifyContent="between">
                      <Typography>Plan Price:</Typography>
                      <Typography sx={{ fontWeight: 600 }}>
                        {formatCurrency(planComparison.comparison.target_price)}
                      </Typography>
                    </Box>
                    
                    {planComparison.comparison.prorated_amount > 0 && (
                      <Box display="flex" justifyContent="between">
                        <Typography>Prorated Amount:</Typography>
                        <Typography sx={{ fontWeight: 600 }}>
                          {formatCurrency(planComparison.comparison.prorated_amount)}
                        </Typography>
                      </Box>
                    )}
                    
                    {planComparison.cost_calculation && (
                      <Box display="flex" justifyContent="between">
                        <Typography sx={{ fontWeight: 600 }}>Total Cost:</Typography>
                        <Typography sx={{ fontWeight: 700, fontSize: '1.2rem' }}>
                          {formatCurrency(planComparison.cost_calculation.total_cost)}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </CardContent>
              </Card>

              {/* Features Preview */}
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                What's included:
              </Typography>
              <List dense>
                {selectedPlan.features.slice(0, 8).map((feature, idx) => (
                  <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircle color="success" sx={{ fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={feature} 
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button 
            onClick={() => setPlanComparisonDialog(false)}
            variant="outlined"
            size="large"
            sx={{ borderRadius: 2, px: 4 }}
          >
            Cancel
          </Button>
          
          {planComparison?.comparison?.action_type === 'upgrade' && (
            <Button 
              variant="contained" 
              onClick={() => handleRazorpayPayment(selectedPlan)} 
              disabled={isProcessingPayment}
              size="large"
              sx={{ 
                borderRadius: 2, 
                px: 4,
                background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)'
              }}
            >
              {isProcessingPayment ? 'Processing...' : 'Upgrade Now'}
            </Button>
          )}
          
          {planComparison?.comparison?.action_type === 'downgrade' && (
            <Button 
              variant="contained" 
              color="warning"
              onClick={() => handleRazorpayPayment(selectedPlan)}
              disabled={isProcessingPayment}
              size="large"
              sx={{ borderRadius: 2, px: 4 }}
            >
              {isProcessingPayment ? 'Processing...' : 'Downgrade'}
            </Button>
          )}
          
          {planComparison?.comparison?.action_type === 'renew' && (
            <Button 
              variant="contained" 
              onClick={() => handleRazorpayPayment(selectedPlan)} 
              disabled={isProcessingPayment}
              size="large"
              sx={{ borderRadius: 2, px: 4 }}
            >
              {isProcessingPayment ? 'Processing...' : 'Renew Plan'}
            </Button>
          )}
          
          {planComparison?.comparison?.action_type === 'new' && (
            <Button 
              variant="contained" 
              onClick={() => handleRazorpayPayment(selectedPlan)} 
              disabled={isProcessingPayment}
              size="large"
              sx={{ borderRadius: 2, px: 4 }}
            >
              {isProcessingPayment ? 'Processing...' : 'Get Started'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        action={confirmDialog.action}
        onConfirm={handleConfirmAction}
        onCancel={() => setConfirmDialog({ open: false, action: null, plan: null })}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SubscriptionManagement; 